import type { CircleHitObject, CirclesChart } from '@shared/circlesChart';
import type { PlayfieldRect } from '../circlesPlayfieldGeometry';
import { getStackOffsets } from '../circlesStacking';
import {
  CIRCLE_RIM_ALPHA,
  CIRCLE_RIM_COLOUR,
  CIRCLE_RIM_RADIUS_MUL,
  CIRCLE_RIM_WIDTH_MUL,
  SHAKE_AMPLITUDE_OSU,
  SHAKE_FREQUENCY,
  SHAKE_LIFE,
} from './circlesRenderConstants';
import {
  comboColour,
  getWithinComboLookup,
  hexToRgba,
} from './circlesRenderColour';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Owns the per-circle shake timers (the visual feedback for an
 * early-click on an approaching circle) and exposes the pure
 * `drawCircle` operation. Shake state is render-only — the engine
 * never sees it, so it lives here rather than in shared state.
 *
 * Followpoints are a separate render pass with their own monotonic
 * cursor state, so they live in {@link FollowpointRenderer} rather
 * than on this class.
 */
export class HitObjectRenderer {
  /**
   * Active shake animations keyed by hit-object id. Each entry's
   * value is the remaining shake-life in seconds, counted down each
   * frame. Populated by {@link registerShake} from the session in
   * response to engine `circle_shake` events.
   *
   * Map (not Set) so multiple early-clicks on the same approaching
   * circle refresh the timer rather than queueing — the player gets
   * continuous wobble feedback if they spam-click prematurely.
   */
  private shakingCircles: Map<string, number> = new Map();
  private reducedMotion: boolean;

  constructor(reducedMotion: boolean) {
    this.reducedMotion = reducedMotion;
  }

  /**
   * Start (or refresh) a horizontal-wobble shake on the named circle.
   * Called by the session in response to engine `circle_shake` events
   * — the player clicked on an approaching but not-yet-hittable
   * circle and we want them to see the target react without
   * resolving it. Skipped under reduced-motion; the engine's
   * `circle_shake` event already triggers the "TOO EARLY!" floater
   * which is informational and not movement-dependent.
   */
  registerShake(hitObjectId: string): void {
    if (this.reducedMotion) {
      return;
    }
    this.shakingCircles.set(hitObjectId, SHAKE_LIFE);
  }

  /** Drop all active shakes on loop wrap so re-played circles start fresh. */
  clearShakes(): void {
    this.shakingCircles.clear();
  }

  /** Age + cull shake timers. */
  updateShakes(dt: number): void {
    if (this.shakingCircles.size === 0) {
      return;
    }
    for (const [id, remaining] of this.shakingCircles) {
      const next = remaining - dt;
      if (next <= 0) {
        this.shakingCircles.delete(id);
      } else {
        this.shakingCircles.set(id, next);
      }
    }
  }

  drawCircle(
    ctx: CanvasRenderingContext2D,
    chart: CirclesChart,
    obj: CircleHitObject,
    t: number,
    field: PlayfieldRect,
    dist: number,
    perfectWindowBeats: number,
    goodWindowBeats: number,
    kiaiScaleBoost: number,
    quality: RenderQuality,
  ): void {
    // Stack offset: when mappers placed two or more circles at near-
    // identical playfield positions in close beat-succession, osu!
    // offsets each subsequent one up-and-left so they form a
    // readable diagonal stack. Purely visual — the engine reads the
    // raw `obj.x/obj.y` for hit-detection and replays.
    const stackOffset = getStackOffsets(chart).get(obj.id);
    const stackDx = stackOffset?.dx ?? 0;
    const stackDy = stackOffset?.dy ?? 0;

    // Shake jitter: an active early-click shake displaces the
    // circle's visual centre horizontally with a damped sin wave.
    // Amplitude scales with remaining life so the wobble peaks on
    // impact and fades smoothly. The shake never moves the
    // hit-detection target — the engine reads `obj.x/obj.y` directly,
    // so a player can still land a clean hit on the original centre
    // once the good window opens.
    const shakeRemaining = this.shakingCircles.get(obj.id) ?? 0;
    let shakeOffsetPx = 0;
    if (shakeRemaining > 0) {
      const phase = (SHAKE_LIFE - shakeRemaining) * SHAKE_FREQUENCY * Math.PI * 2;
      const amplitudeFrac = shakeRemaining / SHAKE_LIFE;
      shakeOffsetPx = Math.sin(phase) * SHAKE_AMPLITUDE_OSU * field.scale * amplitudeFrac;
    }
    const cx = field.x + (obj.x + stackDx) * field.scale + shakeOffsetPx;
    const cy = field.y + (obj.y + stackDy) * field.scale;
    // Kiai wobble: scale the radius up to ~4% larger on each kiai
    // downbeat. Applied to `innerR` so the approach-ring (4×) and
    // body (1×) grow in lockstep — players see the playfield's pulse
    // expressed at every visible circle, not just the bg overlay.
    const kiaiScale = 1 + kiaiScaleBoost;
    const innerR = chart.circleRadius * field.scale * kiaiScale;
    // Approach ring shrinks from `outerR` at t=1 to `innerR` at t=0.
    // 4x matches osu!'s standard approach-ring scale — the iconic
    // "ring rushes in" silhouette every osu! player has internalised.
    const outerR = innerR * 4;
    const approachR = innerR + (outerR - innerR) * Math.max(0, Math.min(1, t));
    const colour = comboColour(chart, obj.comboIndex);

    // Fade-in: ramp alpha 0→1 over the first FADE_IN_FRACTION of the
    // approach window so circles emerge smoothly rather than popping
    // in at full opacity. Matches osu!'s "full opacity at 2/3 of
    // preempt" rule — circles spend the first two thirds of the
    // approach fading in, then the final third at full opacity. The
    // slow read-in is part of why osu circles feel like they "emerge"
    // gracefully instead of popping in. At t=1 (just appeared) alpha
    // is 0; by t=1-FADE_IN_FRACTION alpha is 1 and stays there through
    // the rest of the approach. Already-past circles (t<0) stay at 1.
    const FADE_IN_FRACTION = 0.67;
    const fadeIn =
      t >= 1
        ? 0
        : t <= 1 - FADE_IN_FRACTION
          ? 1
          : (1 - t) / FADE_IN_FRACTION;
    // Approach-ring caps at 90% opacity (osu! stable behaviour). The
    // slight transparency softens the ring so it reads as guide, not
    // foreground — lazer briefly shipped 100% and players reported
    // AR felt harsher; the 90% cap is what made it feel right again.
    const APPROACH_RING_ALPHA_CAP = 0.9;
    const approachAlpha = fadeIn * APPROACH_RING_ALPHA_CAP;

    // Hit-window state: how close is the circle to its perfect timing?
    // Players can't read raw approach-ring shrinkage finely enough to
    // know when they're "in the perfect window", so we convey it with
    // body brightness + a halo ring at the perfect-band radius.
    const absDist = Math.abs(dist);
    const inPerfectWindow = absDist <= perfectWindowBeats;
    const inGoodWindow = absDist <= goodWindowBeats;
    // Smooth 0→1 ramp as the circle enters the good window, peaking at
    // 1 inside the perfect window. Used to scale glow intensity and
    // body alpha so the circle visibly "lights up" right when the
    // player should click.
    const windowProximity = inPerfectWindow
      ? 1
      : inGoodWindow
        ? 1 - (absDist - perfectWindowBeats) / (goodWindowBeats - perfectWindowBeats)
        : 0;

    ctx.save();
    // Approach ring — capped at APPROACH_RING_ALPHA_CAP so the ring
    // never reaches full opacity even after the fade-in completes.
    // Drawn first so the body/glow/number layers below sit on top.
    ctx.globalAlpha = approachAlpha;
    ctx.strokeStyle = hexToRgba(colour, 0.7);
    ctx.lineWidth = Math.max(2, innerR * 0.08);
    ctx.beginPath();
    ctx.arc(cx, cy, approachR, 0, Math.PI * 2);
    ctx.stroke();

    // Body / glow / number all share the un-capped fade-in alpha so
    // the actual hit target reads cleanly even with the softened ring.
    ctx.globalAlpha = fadeIn;
    // Hit body — a filled disc with a dark inner so the combo number reads.
    // Body alpha ramps with windowProximity so the circle visibly fills
    // in as it enters the timing window. Floor (0.5) keeps the disc
    // legible from first appearance — players were unable to read
    // combo digits on approach when the floor was 0.22 (a 4× too-low
    // body alpha vs the surrounding bg gradient at that opacity).
    const bodyAlpha = 0.5 + 0.35 * windowProximity;
    ctx.fillStyle = hexToRgba(colour, bodyAlpha);
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = colour;
    ctx.lineWidth = Math.max(2, innerR * 0.12);
    // Glow intensity ramps from 16 (idle) → 32 (perfect window). Gives
    // the player a clear "click NOW" pulse without changing the actual
    // hit-window math. Skipped on low tier — with 8-12 visible circles
    // each pushing a shadowBlur stroke this was a dominant per-frame
    // cost on mobile. Stroke alone still carries the timing signal.
    if (quality.chromeShadows) {
      ctx.shadowColor = colour;
      ctx.shadowBlur = 16 + 16 * windowProximity;
    }
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Dark contrast rim just outside the coloured border. Drawn AFTER
    // the body/border (and with the glow's shadow already reset) so it
    // lands as a crisp dark edge on top of this circle's own glow AND on
    // top of any circle already painted behind it — that second part is
    // the whole point: where this circle overlaps an earlier one, the
    // rim cuts a clean dark crescent between them so stacked / sneaking
    // circles read as separate tokens instead of merging into a blob.
    // A stroke (not a filled disc) keeps the body's own translucency
    // intact — it only darkens the thin annulus outside the body.
    ctx.strokeStyle = hexToRgba(CIRCLE_RIM_COLOUR, CIRCLE_RIM_ALPHA);
    ctx.lineWidth = Math.max(2, innerR * CIRCLE_RIM_WIDTH_MUL);
    ctx.beginPath();
    ctx.arc(cx, cy, innerR * CIRCLE_RIM_RADIUS_MUL, 0, Math.PI * 2);
    ctx.stroke();

    if (inPerfectWindow) {
      // Bright halo just outside the body when the circle is in the
      // perfect timing window. Single-frame visual cue that aligns
      // precisely with the engine's perfect-judgement boundary.
      ctx.strokeStyle = hexToRgba('#ffffff', 0.85);
      ctx.lineWidth = Math.max(1.5, innerR * 0.06);
      ctx.beginPath();
      ctx.arc(cx, cy, innerR * 1.18, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Combo number inside the circle: the player's within-combo
    // position (1, 2, 3, …), reset on `newCombo`. NB: this is NOT
    // `obj.comboIndex` — that field is a colour-group counter shared
    // across every object in one combo group, so using it here would
    // print the same digit for every circle in a stream. We derive
    // the displayed number from a chart-wide lookup that walks hit
    // objects in beat order.
    //
    // Lazer-style depth-of-field: the digit dims while the circle is
    // still far from its hit time and brightens as it enters the
    // timing window. Drives the eye toward "the one to tap next" in
    // dense streams without changing what the player can hit. Floor
    // at NUMBER_DIM_ALPHA so the digit stays visible from first
    // appearance — set above the body-alpha floor so the digit always
    // reads as the brightest element inside the circle.
    //
    // Asymmetric: only dim on the EARLY side (`dist > 0`, circle
    // hasn't reached its hit beat yet). Once we're at-or-past the
    // hit beat (`dist <= 0`), keep the digit fully bright through the
    // rest of the OK window — a player who's late on a dense stream
    // shouldn't see the digit dim again as that misreads as "not
    // yet" and invites a miss.
    const NUMBER_DIM_ALPHA = 0.7;
    const numberAlpha =
      dist <= 0 ? 1 : NUMBER_DIM_ALPHA + (1 - NUMBER_DIM_ALPHA) * windowProximity;
    const withinCombo = getWithinComboLookup(chart).get(obj.id) ?? 1;
    const fontSize = Math.max(12, innerR * 0.9);
    ctx.font = `bold ${Math.round(fontSize)}px 'Rajdhani', 'Orbitron', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Dark stroke underneath the white fill so the digit reads on
    // every combo colour — without this, the bright body of an orange
    // / amber / yellow circle washes out the white glyph and the
    // number reads as a ghost (especially at half-window proximity
    // where the body alpha climbs past 0.5). Stroke width scales with
    // the digit size so small distant numbers don't drown in their
    // own outline.
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    ctx.lineWidth = Math.max(2, fontSize * 0.16);
    ctx.strokeStyle = `rgba(15, 18, 32, ${numberAlpha * 0.85})`;
    ctx.strokeText(String(withinCombo), cx, cy);
    ctx.fillStyle = `rgba(255, 255, 255, ${numberAlpha})`;
    ctx.fillText(String(withinCombo), cx, cy);

    if (obj.newCombo) {
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(cx, cy - innerR + 4, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
